import React from "react";
import styled from "styled-components";
import tw from "twin.macro";
import { useTranslation } from "react-i18next";

const Wrapper = tw.section`w-full`;
const StatsBar = styled.div`
  ${tw`w-full`}
  background: #FFFFFF;
`;
const StatsGrid = tw.div`max-w-screen-xl mx-auto px-4 md:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center`;
const StatValue = tw.div`text-xl md:text-2xl font-extrabold text-[#edbf03]`;
const StatLabel = tw.div`text-xs md:text-sm text-gray-600 mt-1`;

const Inner = tw.div`max-w-screen-xl mx-auto px-4 md:px-8 py-8 md:py-12 text-center`;
const Heading = tw.h2`text-2xl md:text-3xl font-extrabold text-[#133B8C]`;
const Sub = tw.p`mt-3 text-gray-500 text-sm md:text-base max-w-2xl mx-auto`;

const Features = tw.div`mt-10 grid grid-cols-1 md:grid-cols-3 gap-8`;
const Feature = styled.div`
  ${tw`flex flex-col items-center text-center p-6 rounded-2xl bg-white`}
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  transition: transform 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }

  &:focus-within {
    box-shadow: 0 0 0 3px rgba(19, 59, 140, 0.2), 0 6px 20px rgba(0, 0, 0, 0.06);
  }
`;
const IconWrap = styled.div`
  ${tw`h-16 w-16 rounded-full flex items-center justify-center mb-4`}
  background: #edbf03;
`;
const FeatureTitle = tw.h3`font-semibold text-gray-800`;
const FeatureDesc = tw.p`mt-2 text-sm text-gray-500 max-w-xs`;

// Simple inline icons
const HandIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#133b8c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 13v-3a2 2 0 0 1 4 0v3"/>
    <path d="M12 13v-5a2 2 0 0 1 4 0v5"/>
    <path d="M16 13v-3a2 2 0 0 1 4 0v2"/>
    <path d="M2 13s1.5 5 10 5 10-5 10-5"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#133b8c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);
const LeafIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#133b8c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21c1-7 7-13 14-14 0 7-7 14-14 14z"/>
    <path d="M14 7c0 4-3 7-7 7"/>
  </svg>
);

// mode: 'brand' for original cosmetic brand messaging, 'agency' for Agency WMG content
const WhySection = ({
  stats = [
    { value: "400", labelKey: "why.stats.products" },
    { value: "12", labelKey: "why.stats.categories" },
    { value: "50", labelKey: "why.stats.satisfiedClients" },
    { value: "60", labelKey: "why.stats.projects" }
  ],
  mode = "agency" // default to agency per latest request
}) => {
  const { t } = useTranslation();

  const isAgency = mode === "agency";
  const baseKey = isAgency ? "agencyWhy" : "why";

  const features = isAgency
    ? [
        { Icon: HandIcon, title: t(`${baseKey}.features.communication.title`, "Communication continue et efficace"), desc: t(`${baseKey}.features.communication.desc`, "Communication permanente pour la réussite de chaque projet.") },
        { Icon: ShieldIcon, title: t(`${baseKey}.features.quality.title`, "Services de haute qualité"), desc: t(`${baseKey}.features.quality.desc`, "Nous visons uniquement des résultats de haute qualité.") },
        { Icon: LeafIcon, title: t(`${baseKey}.features.experience.title`, "Des années d'expérience"), desc: t(`${baseKey}.features.experience.desc`, "+15 ans d'expérience en marketing traditionnel & digital.") }
      ]
    : [
        { Icon: HandIcon, title: t("why.features.natural.title", "Natural"), desc: t("why.features.natural.desc", "Gentle, natural ingredients.") },
        { Icon: ShieldIcon, title: t("why.features.noSideEffect.title", "No Side effect"), desc: t("why.features.noSideEffect.desc", "Formulas designed for optimal tolerance.") },
        { Icon: LeafIcon, title: t("why.features.organic.title", "100% Organic"), desc: t("why.features.organic.desc", "Certified organic quality.") }
      ];

  return (
    <Wrapper>
     

      <Inner>
        <Heading>{t(`${baseKey}.heading`, isAgency ? "POURQUOI AGENCY WMG ?" : "WHY SNAILLIA?")}</Heading>
        <Sub>{t(`${baseKey}.subtitle`, isAgency ? "Votre partenaire de croissance et de performance marketing." : "Votre peau mérite des soins naturels et efficaces.")}</Sub>

        <Features>
          {features.map(({ Icon, title, desc }, idx) => (
            <Feature key={idx}>
              <IconWrap>
                <Icon />
              </IconWrap>
              <FeatureTitle>{title}</FeatureTitle>
              <FeatureDesc>{desc}</FeatureDesc>
            </Feature>
          ))}
        </Features>
      </Inner>
       <StatsBar>
        <StatsGrid>
          {stats.map((s, i) => (
            <div key={i}>
              <StatValue>{s.value}</StatValue>
              <StatLabel>{t(s.labelKey)}</StatLabel>
            </div>
          ))}
        </StatsGrid>
      </StatsBar>
    </Wrapper>
  );
};

export default WhySection;
